import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Row, Button, Form, Alert, Accordion, Badge, Card, Modal } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { config } from '../config';
import MainAppWrap from '../Layout/MainAppWrap';
import SimpleHeader from '../components/SimpleHeader';
import SweetLayout from '../Layout/SweetLayout';
import Loader from '../components/loader';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  SortingState,
} from '@tanstack/react-table';
import './QueryBuilder.css';

interface Column {
  column_name: string;
  data_type: string;
  character_maximum_length: number | null;
  is_nullable: string;
  column_default: string | null;
}

interface Constraint {
  constraint_name: string;
  constraint_type: string;
  column_name: string | null;
}

interface TableSchema {
  table_name: string;
  columns: Column[];
  constraints: Constraint[];
}

interface ValidationResult {
  is_safe: boolean;
  threat_level: string;
  confidence_score: number;
  reasoning: string;
  detected_patterns?: string[];
  recommendations?: string;
}

interface QueryResult {
  success: boolean;
  data: any[];
  row_count: number;
  columns: string[];
  validation_result?: ValidationResult;
  execution_time_ms?: number;
}

interface ValidateResponse {
  success: boolean;
  validation_result?: ValidationResult;
  rule_based_issues?: string[];
  error?: string;
}

const QueryBuilder: React.FC = () => {
  const { getIdTokenClaims, user } = useAuth0();
  
  // State
  const [loading, setLoading] = useState(false);
  const [tables, setTables] = useState<string[]>([]);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [tableSchema, setTableSchema] = useState<TableSchema | null>(null);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [query, setQuery] = useState('');
  const [queryResult, setQueryResult] = useState<QueryResult | null>(null);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [executing, setExecuting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidateResponse | null>(null);
  const [isQueryValidated, setIsQueryValidated] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [showValidationDetails, setShowValidationDetails] = useState(true);
  const [selectedJsonData, setSelectedJsonData] = useState<any>(null);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(250);
  const [isResizing, setIsResizing] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Check access on mount
  useEffect(() => {
    if (user) {
      const email = (user as any).email || '';
      const roles = (user as any).ept_roles || [];
      const accountId = (user as any)?.ept_app_metadata?.account_id;

      // For now, allow any Admin user to see the page
      // Backend will enforce stricter rules: @ept.ai email, EPTAdmin role, account 1 or 426
      if (!roles.includes('Admin') && !roles.includes('EPTAdmin')) {
        setAccessDenied(true);
      }
    }
  }, [user]);

  // Fetch tables list
  const fetchTables = useCallback(async () => {
    if (accessDenied) return;
    
    setLoading(true);
    
    try {
      const token = await getIdTokenClaims();
      const idToken = token?.__raw;

      const response = await axios.get(
        `${config.baseURL}/admin/v1/query_builder/tables`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        setTables(response.data.tables || []);
      } else {
        console.error('Error fetching tables:', response.data.error);
      }
    } catch (error: any) {
      console.error('Error fetching tables:', error);
      if (error.response?.status === 403) {
        setAccessDenied(true);
      }
    } finally {
      setLoading(false);
    }
  }, [getIdTokenClaims, accessDenied]);

  // Fetch table schema
  const fetchTableSchema = useCallback(async (tableName: string) => {
    setLoadingSchema(true);
    setTableSchema(null);
    
    try {
      const token = await getIdTokenClaims();
      const idToken = token?.__raw;

      const response = await axios.get(
        `${config.baseURL}/admin/v1/query_builder/tables/${tableName}/schema`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        setTableSchema(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching table schema:', error);
    } finally {
      setLoadingSchema(false);
    }
  }, [getIdTokenClaims]);

  // Validate query
  const validateQuery = useCallback(async () => {
    if (!query.trim()) {
      setQueryError('Query cannot be empty');
      return;
    }

    setValidating(true);
    setValidationResult(null);
    setIsQueryValidated(false);
    setQueryError(null);

    try {
      const token = await getIdTokenClaims();
      const idToken = token?.__raw;

      const response = await axios.post(
        `${config.baseURL}/admin/v1/query_builder/validate`,
        { query: query.trim() },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        setValidationResult(response.data);
        
        // Check if validation passed
        const hasRuleIssues = response.data.rule_based_issues && 
          response.data.rule_based_issues.length > 0;
        const aiValidationFailed = response.data.validation_result && 
          !response.data.validation_result.is_safe;
        
        if (hasRuleIssues || aiValidationFailed) {
          setIsQueryValidated(false);
        } else {
          setIsQueryValidated(true);
        }
      } else {
        setQueryError(response.data.error || 'Validation failed');
      }
    } catch (error: any) {
      console.error('Error validating query:', error);
      setQueryError(
        error.response?.data?.error || 
        error.message || 
        'Failed to validate query'
      );
    } finally {
      setValidating(false);
    }
  }, [query, getIdTokenClaims]);

  // Execute query
  const executeQuery = useCallback(async () => {
    if (!query.trim()) {
      setQueryError('Query cannot be empty');
      return;
    }

    if (!isQueryValidated) {
      setQueryError('Please validate your query before executing');
      return;
    }

    setExecuting(true);
    setQueryError(null);
    setQueryResult(null);

    try {
      const token = await getIdTokenClaims();
      const idToken = token?.__raw;

      const response = await axios.post(
        `${config.baseURL}/admin/v1/query_builder/query`,
        { 
          query: query.trim(),
          skip_ai_validation: false 
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        setQueryResult(response.data);
      } else {
        setQueryError(response.data.error || 'Query execution failed');
      }
    } catch (error: any) {
      console.error('Error executing query:', error);
      setQueryError(
        error.response?.data?.error || 
        error.message || 
        'Failed to execute query'
      );
    } finally {
      setExecuting(false);
    }
  }, [query, isQueryValidated, getIdTokenClaims]);

  // Handle table selection
  const handleTableClick = (tableName: string) => {
    setSelectedTable(tableName);
    fetchTableSchema(tableName);
  };

  // Reset validation when query changes
  useEffect(() => {
    setIsQueryValidated(false);
    setValidationResult(null);
  }, [query]);

  // Create table columns dynamically from query results
  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>(() => {
    if (!queryResult || !queryResult.columns) return [];

    return queryResult.columns.map((col) => ({
      accessorKey: col,
      header: col,
      cell: (info) => {
        const value = info.getValue();
        
        if (value === null || value === undefined) {
          return <span className="null-value">NULL</span>;
        }
        
        if (typeof value === 'object') {
          return (
            <button
              className="json-view-btn"
              onClick={() => {
                setSelectedJsonData(value);
                setShowJsonModal(true);
              }}
            >
              <i className="fa-solid fa-code me-1"></i>
              View JSON
            </button>
          );
        }
        
        return <span className="cell-value" title={String(value)}>{String(value)}</span>;
      },
      size: 200,
      minSize: 100,
      maxSize: 600,
    }));
  }, [queryResult]);

  // Create table instance
  const table = useReactTable({
    data: queryResult?.data || [],
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
  });

  // Handle sidebar resize
  const handleMouseDown = useCallback(() => {
    setIsResizing(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isResizing) {
        const newWidth = e.clientX;
        if (newWidth >= 200 && newWidth <= 600) {
          setSidebarWidth(newWidth);
        }
      }
    },
    [isResizing]
  );

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Download CSV function
  const downloadCSV = useCallback(() => {
    if (!queryResult || !queryResult.data || queryResult.data.length === 0) {
      return;
    }

    const columns = queryResult.columns;
    const rows = queryResult.data;

    // Create CSV content
    const csvContent = [
      // Header row
      columns.join(','),
      // Data rows
      ...rows.map((row) =>
        columns
          .map((col) => {
            const value = row[col];
            if (value === null || value === undefined) {
              return '';
            }
            if (typeof value === 'object') {
              return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
            }
            const stringValue = String(value);
            // Escape quotes and wrap in quotes if contains comma, quote, or newline
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          })
          .join(',')
      ),
    ].join('\n');

    // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `query_results_${new Date().getTime()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Revoke the blob URL to prevent memory leak
      URL.revokeObjectURL(url);
  }, [queryResult]);

  // Load tables on mount
  useEffect(() => {
    if (!accessDenied) {
      fetchTables();
    }
  }, [fetchTables, accessDenied]);

  // Render access denied message
  if (accessDenied) {
    return (
      <SweetLayout>
        <Container fluid className="query-builder-container">
          <Alert variant="danger">
            <Alert.Heading>Access Denied</Alert.Heading>
            <p>
              Query Builder is only available to authorized users.
            </p>
            <hr />
            <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
              <strong>Requirements:</strong>
            </p>
            <ul className="mt-2 mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
              <li>Must have @ept.ai email address</li>
              <li>Must have EPTAdmin role</li>
              <li>Must be in account 1 or 426</li>
            </ul>
          </Alert>
        </Container>
      </SweetLayout>
    );
  }

  return (
    <MainAppWrap>
      <div className="main-content">
        <SimpleHeader title="SQL Query Builder" />
        <Container fluid className="query-builder-container">
          {/* Page Header */}
          <div className="query-builder-page-header">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <p className="query-builder-subtitle">
                  <i className="fa-solid fa-database me-2"></i>
                  Execute read-only SQL queries with AI-powered security validation
                </p>
              </div>
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="sidebar-toggle-btn"
              >
                <i className={`fa-solid fa-${sidebarCollapsed ? 'chevron-right' : 'chevron-left'}`}></i>
                {sidebarCollapsed ? ' Show Tables' : ' Hide Tables'}
              </Button>
            </div>
          </div>

        <Row className="query-builder-body">
          {/* Left sidebar - Tables list */}
          {!sidebarCollapsed && (
            <div 
              className="query-builder-sidebar" 
              style={{ width: `${sidebarWidth}px`, minWidth: '200px', maxWidth: '600px' }}
            >
              <div className="tables-panel">
                <div className="tables-header">
                  <h5>
                    <i className="fa-solid fa-table me-2"></i>
                    Tables
                  </h5>
                  <Badge bg="primary" pill>{tables.length}</Badge>
                </div>
                
                {loading ? (
                  <div className="text-center py-4">
                    <Loader />
                  </div>
                ) : (
                  <Accordion className="tables-accordion" flush>
                    {tables.map((tableName, index) => (
                      <Accordion.Item
                        key={tableName}
                        eventKey={index.toString()}
                        className="table-accordion-item"
                      >
                        <Accordion.Header
                          onClick={() => handleTableClick(tableName)}
                          className="table-accordion-header"
                        >
                          <div className="d-flex align-items-center w-100">
                            <i className="fa-solid fa-table me-2 text-primary"></i>
                            <span className="table-name-text">{tableName}</span>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body className="table-accordion-body">
                          {loadingSchema && selectedTable === tableName ? (
                            <div className="text-center py-3">
                              <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            </div>
                          ) : tableSchema && selectedTable === tableName ? (
                            <div className="table-schema-content">
                              {/* Columns Section */}
                              <div className="schema-section">
                                <div className="schema-section-header">
                                  <i className="fa-solid fa-columns me-2"></i>
                                  <span>Columns</span>
                                  <Badge bg="secondary" pill className="ms-auto">
                                    {tableSchema.columns.length}
                                  </Badge>
                                </div>
                                <div className="columns-grid">
                                  {tableSchema.columns.map((col) => (
                                    <div key={col.column_name} className="column-card">
                                      <div className="column-card-header">
                                        <i className="fa-solid fa-database me-2"></i>
                                        <span className="column-name-text">{col.column_name}</span>
                                      </div>
                                      <div className="column-card-body">
                                        <code className="column-type-badge">
                                          {col.data_type}
                                          {col.character_maximum_length && `(${col.character_maximum_length})`}
                                        </code>
                                        {col.is_nullable === 'NO' && (
                                          <Badge bg="danger" className="ms-2">
                                            NOT NULL
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              
                              {/* Constraints Section */}
                              {tableSchema.constraints && tableSchema.constraints.length > 0 && (
                                <div className="schema-section mt-3">
                                  <div className="schema-section-header">
                                    <i className="fa-solid fa-link me-2"></i>
                                    <span>Constraints</span>
                                    <Badge bg="secondary" pill className="ms-auto">
                                      {tableSchema.constraints.length}
                                    </Badge>
                                  </div>
                                  <div className="constraints-grid">
                                    {tableSchema.constraints
                                      .filter(c => c.constraint_type !== 'CHECK' || c.column_name)
                                      .map((constraint, idx) => (
                                        <div key={idx} className="constraint-card">
                                          <Badge 
                                            bg={
                                              constraint.constraint_type === 'PRIMARY KEY' ? 'primary' :
                                              constraint.constraint_type === 'FOREIGN KEY' ? 'info' :
                                              constraint.constraint_type === 'UNIQUE' ? 'success' :
                                              'secondary'
                                            }
                                            className="constraint-badge"
                                          >
                                            {constraint.constraint_type === 'PRIMARY KEY' ? 'PK' :
                                             constraint.constraint_type === 'FOREIGN KEY' ? 'FK' :
                                             constraint.constraint_type === 'UNIQUE' ? 'UQ' :
                                             constraint.constraint_type}
                                          </Badge>
                                          {constraint.column_name && (
                                            <span className="constraint-column">
                                              {constraint.column_name}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : null}
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                )}
              </div>

              {/* Resize handle */}
              <div 
                className="sidebar-resize-handle"
                onMouseDown={handleMouseDown}
              />
            </div>
          )}

          {/* Main content - Query editor and results */}
          <div 
            className="query-builder-main" 
            style={{ 
              width: sidebarCollapsed ? '100%' : `calc(100% - ${sidebarWidth}px)`,
              marginLeft: sidebarCollapsed ? '0' : '0'
            }}
          >
            {/* Query Editor */}
            <div className="query-editor-card">
              <div className="query-editor-header">
                <div className="d-flex align-items-center">
                  <i className="fa-solid fa-code me-2"></i>
                  <span>SQL Query</span>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={validateQuery}
                    disabled={validating || !query.trim()}
                    className="action-btn validate-btn"
                  >
                    {validating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Validating...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-shield-halved me-2"></i>
                        Validate Query
                      </>
                    )}
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={executeQuery}
                    disabled={executing || !query.trim() || !isQueryValidated}
                    className="action-btn execute-btn"
                  >
                    {executing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Executing...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-play me-2"></i>
                        Execute Query
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="query-editor-body">
                <Form.Control
                  as="textarea"
                  rows={12}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Enter your SQL query here... (SELECT and EXPLAIN only)&#10;&#10;Example:&#10;SELECT * FROM users LIMIT 10;"
                  className="query-textarea-input"
                />
                <div className="query-editor-footer">
                  <i className="fa-solid fa-info-circle me-2"></i>
                  <small>
                    Click "Validate Query" first to check security. Execute button will be enabled only after successful validation.
                  </small>
                </div>
              </div>
            </div>

            {/* Validation Result */}
            {validationResult && (
              <Card className="validation-result-card">
                <Card.Header 
                  className={`validation-result-header ${
                    validationResult.rule_based_issues && validationResult.rule_based_issues.length > 0
                      ? 'validation-failed'
                      : validationResult.validation_result && !validationResult.validation_result.is_safe
                      ? 'validation-failed'
                      : 'validation-success'
                  }`}
                  onClick={() => setShowValidationDetails(!showValidationDetails)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <i className={`fa-solid ${
                        validationResult.rule_based_issues && validationResult.rule_based_issues.length > 0
                          ? 'fa-times-circle'
                          : validationResult.validation_result && !validationResult.validation_result.is_safe
                          ? 'fa-times-circle'
                          : 'fa-check-circle'
                      } me-2`}></i>
                      <span className="validation-result-title">Validation Result</span>
                    </div>
                    <i className={`fa-solid fa-chevron-${showValidationDetails ? 'up' : 'down'}`}></i>
                  </div>
                </Card.Header>
                {showValidationDetails && (
                <Card.Body>
                  {/* Rule-based issues */}
                  {validationResult.rule_based_issues && validationResult.rule_based_issues.length > 0 && (
                    <Alert variant="danger" className="mb-3">
                      <Alert.Heading className="h6">
                        <i className="fa-solid fa-ban me-2"></i>
                        Rule-Based Validation Failed
                      </Alert.Heading>
                      <ul className="mb-0">
                        {validationResult.rule_based_issues.map((issue, idx) => (
                          <li key={idx}>{issue}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}

                  {/* AI validation result */}
                  {validationResult.validation_result && (
                    <div>
                      <div className="d-flex align-items-center mb-3">
                        <Badge 
                          bg={
                            validationResult.validation_result.threat_level === 'safe' 
                              ? 'success' 
                              : validationResult.validation_result.threat_level === 'medium'
                              ? 'warning'
                              : 'danger'
                          }
                          className="me-2"
                        >
                          {validationResult.validation_result.threat_level.toUpperCase()}
                        </Badge>
                        <span className="text-muted">
                          Confidence: {(validationResult.validation_result.confidence_score * 100).toFixed(0)}%
                        </span>
                      </div>

                      <div className="mb-3">
                        <strong>Analysis:</strong>
                        <p className="mt-2 mb-0">{validationResult.validation_result.reasoning}</p>
                      </div>

                      {validationResult.validation_result.detected_patterns && 
                       validationResult.validation_result.detected_patterns.length > 0 && (
                        <div className="mb-3">
                          <strong>Detected Patterns:</strong>
                          <ul className="mt-2 mb-0">
                            {validationResult.validation_result.detected_patterns.map((pattern, idx) => (
                              <li key={idx}>{pattern}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {validationResult.validation_result.recommendations && (
                        <div>
                          <strong>Recommendations:</strong>
                          <p className="mt-2 mb-0">{validationResult.validation_result.recommendations}</p>
                        </div>
                      )}
                    </div>
                  )}
                </Card.Body>
                )}
              </Card>
            )}

            {/* Query Error */}
            {queryError && (
              <Alert variant="danger" dismissible onClose={() => setQueryError(null)}>
                <Alert.Heading>
                  <i className="fa-solid fa-exclamation-triangle me-2"></i>
                  Query Error
                </Alert.Heading>
                <pre style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                  {queryError}
                </pre>
              </Alert>
            )}

            {/* Query Results */}
            {queryResult && (
              <div className="query-results-card">
                <div className="query-results-header">
                  <div className="d-flex align-items-center">
                    <i className="fa-solid fa-table me-2"></i>
                    <span>Query Results</span>
                  </div>
                  <div className="d-flex gap-2 align-items-center">
                    {queryResult.execution_time_ms && (
                      <Badge bg="info" className="metric-badge">
                        <i className="fa-solid fa-clock me-1"></i>
                        {queryResult.execution_time_ms.toFixed(2)}ms
                      </Badge>
                    )}
                    <Badge bg="success" className="metric-badge">
                      <i className="fa-solid fa-database me-1"></i>
                      {queryResult.row_count} row{queryResult.row_count !== 1 ? 's' : ''}
                    </Badge>
                    {queryResult.data.length > 0 && (
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={downloadCSV}
                        className="download-csv-btn"
                      >
                        <i className="fa-solid fa-download me-2"></i>
                        Download CSV
                      </Button>
                    )}
                  </div>
                </div>

                <div className="query-results-body">
                  {queryResult.data.length === 0 ? (
                    <Alert variant="info" className="empty-results-alert">
                      <i className="fa-solid fa-info-circle me-2"></i>
                      Query executed successfully, but returned no rows.
                    </Alert>
                  ) : (
                    <>
                      <div className="results-table-wrapper">
                        <table className="results-table">
                          <thead>
                            {table.getHeaderGroups().map(headerGroup => (
                              <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                  <th 
                                    key={header.id}
                                    style={{ width: header.getSize() }}
                                  >
                                    <div 
                                      className={`column-header ${header.column.getCanSort() ? 'sortable' : ''}`}
                                      onClick={header.column.getToggleSortingHandler()}
                                    >
                                      <i className="fa-solid fa-database me-2"></i>
                                      {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                      )}
                                      {header.column.getIsSorted() && (
                                        <span className="sort-indicator ms-2">
                                          {header.column.getIsSorted() === 'asc' ? (
                                            <i className="fa-solid fa-arrow-up"></i>
                                          ) : (
                                            <i className="fa-solid fa-arrow-down"></i>
                                          )}
                                        </span>
                                      )}
                                    </div>
                                    <div
                                      className="column-resizer"
                                      onMouseDown={header.getResizeHandler()}
                                      onTouchStart={header.getResizeHandler()}
                                    />
                                  </th>
                                ))}
                              </tr>
                            ))}
                          </thead>
                          <tbody>
                            {table.getRowModel().rows.map(row => (
                              <tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                  <td 
                                    key={cell.id}
                                    style={{ width: cell.column.getSize() }}
                                  >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {table.getPageCount() > 1 && (
                        <div className="pagination-container">
                          <div className="pagination-info">
                            Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                            {Math.min(
                              (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                              table.getFilteredRowModel().rows.length
                            )} of{' '}
                            {table.getFilteredRowModel().rows.length} rows
                          </div>
                          <div className="pagination-controls">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => table.setPageIndex(0)}
                              disabled={!table.getCanPreviousPage()}
                            >
                              <i className="fa-solid fa-angles-left"></i>
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => table.previousPage()}
                              disabled={!table.getCanPreviousPage()}
                            >
                              <i className="fa-solid fa-angle-left"></i>
                            </Button>
                            <span className="pagination-page-info">
                              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                            </span>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => table.nextPage()}
                              disabled={!table.getCanNextPage()}
                            >
                              <i className="fa-solid fa-angle-right"></i>
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                              disabled={!table.getCanNextPage()}
                            >
                              <i className="fa-solid fa-angles-right"></i>
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </Row>

        {/* JSON Modal */}
        <Modal 
          show={showJsonModal} 
          onHide={() => setShowJsonModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton className="json-modal-header">
            <Modal.Title>
              <i className="fa-solid fa-code me-2"></i>
              JSON Data
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="json-modal-body">
            <pre className="json-pre">
              {JSON.stringify(selectedJsonData, null, 2)}
            </pre>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowJsonModal(false)}
            >
              Close
            </Button>
            <Button 
              variant="primary" 
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(selectedJsonData, null, 2));
              }}
            >
              <i className="fa-solid fa-copy me-2"></i>
              Copy to Clipboard
            </Button>
          </Modal.Footer>
        </Modal>
        </Container>
      </div>
    </MainAppWrap>
  );
};

export default QueryBuilder;
